import React, { createContext, useContext, useEffect, useState } from "react";
import { User as FirebaseUser, signInWithPopup, signOut, GoogleAuthProvider, GithubAuthProvider, OAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/lib/firebase";

interface AuthContextType {
  user: FirebaseUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: Error | null;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithTestAccount?: () => Promise<void>; // Only available in development
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isInitialized: false,
  error: null,
  signInWithGoogle: async () => {},
  signInWithGithub: async () => {},
  signInWithApple: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const googleProvider = new GoogleAuthProvider();
  const githubProvider = new GithubAuthProvider();
  const appleProvider = new OAuthProvider("apple.com");

  useEffect(() => {
    // Wait for Firebase to be initialized
    if (!window.firebaseInitialized) {
      const checkInterval = setInterval(() => {
        if (window.firebaseInitialized) {
          clearInterval(checkInterval);
          setIsInitialized(true);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }

    setIsInitialized(true);
    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        setUser(user);
        setIsLoading(false);
        if (user) {
          console.log("User signed in:", user.email);
        }
      },
      (error) => {
        setError(error);
        setIsLoading(false);
        console.error("Auth state change error:", error);
      },
    );

    return unsubscribe;
  }, []);

  const handleAuthError = (error: Error) => {
    setError(error);
    console.error("Auth error:", error);
  };

  const handleSignInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      handleAuthError(error as Error);
    }
  };

  const handleSignInWithGithub = async () => {
    try {
      await signInWithPopup(auth, githubProvider);
    } catch (error) {
      handleAuthError(error as Error);
    }
  };

  const handleSignInWithApple = async () => {
    try {
      await signInWithPopup(auth, appleProvider);
    } catch (error) {
      handleAuthError(error as Error);
    }
  };

  const handleSignInWithTestAccount = async () => {
    if (
      !import.meta.env.DEV ||
      import.meta.env.VITE_USE_FIREBASE_EMULATOR !== "true"
    ) {
      console.error(
        "Test account login only available in development with emulator",
      );
      return;
    }

    const testEmail = "test@example.com";
    const testPassword = "password123";

    try {
      console.log("🔑 Attempting to sign in with test account...");

      try {
        // Try to create the account first (in case it doesn't exist)
        console.log("Creating test account...");
        await createUserWithEmailAndPassword(auth, testEmail, testPassword);
        console.log("✅ Created new test account");
      } catch (createError: any) {
        // If error is because account exists, that's fine
        if (createError.code !== "auth/email-already-in-use") {
          throw createError;
        }
        console.log("Test account already exists");
      }

      // Now sign in
      console.log("Signing in with test account...");
      await signInWithEmailAndPassword(auth, testEmail, testPassword);
      console.log("✅ Signed in successfully");
    } catch (error) {
      console.error("❌ Test account error:", error);
      handleAuthError(error as Error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      handleAuthError(error as Error);
    }
  };

  // Show nothing while Firebase is initializing
  if (!isInitialized) {
    return null;
  }

  // Show loading state while auth state is being determined
  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isInitialized,
        error,
        signInWithGoogle: handleSignInWithGoogle,
        signInWithGithub: handleSignInWithGithub,
        signInWithApple: handleSignInWithApple,
        ...(import.meta.env.DEV && {
          signInWithTestAccount: handleSignInWithTestAccount,
        }),
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
